import React, { memo } from "react";
import {
    Box,
    Button,
    Header,
    Icon,
    Link,
    SpaceBetween,
    Spinner,
    Tabs,
} from "@cloudscape-design/components";
import {
    IToolbarButtonActiveContentType,
    IToolbarButtonProps,
    IToolbarButtonStatefulModalProps,
    IToolbarButtonType,
    ToolbarActiveStateContentProps,
    ToolbarContainerItemContent,
    useToolbarCollapseStore,
} from "@amzn/aws-euc-ui";
import {
    FEEDBACK_EMAIL_ADDRESS,
    ToolbarItemId,
    ToolbarMode,
    ToolbarProfileTabId,
} from "@constants/toolbar";
import { AppStreamSDK } from "@utils/AppStreamSDK";
import { Trans, useTranslation } from "react-i18next";
import { SessionPerformanceTabContent } from "./SessionPerformanceTabContent";
import { ValueWithLabel } from "@components/toolbar/ValueWithLabel";
import { HelpTabContent } from "./HelpTabContent";
import { useToolbarProfileTabIdStore } from "@components/toolbar/useToolbarProfileTabIdStore";
import { CopyButton } from "@components/general/CopyButton";
import { useSessionDataStore } from "@hooks/useSessionDataStore";
import { useEmitSessionPerformanceMetrics } from "@components/toolbar/useEmitSessionPerformanceMetrics";
import {
    getRebrandedMessageIdIfExists,
    getRebrandedTranslation,
} from "@utils/i18nUtils";
import { getCustomBrandingConfig } from "@utils/configurationUtils";

/**
 * Profile - About WorkSpaces Web tab content
 */
const AboutTabContent = (): JSX.Element => {
    const { t } = useTranslation();
    return (
        <ValueWithLabel
            label={getRebrandedTranslation("login.icon.alt", t)}
            valueTestId={"about-wsw"}
        >
            <Trans
                i18nKey={getRebrandedMessageIdIfExists(
                    "toolbar.item.user.content.tab.about.content"
                )}
            />
        </ValueWithLabel>
    );
};

/**
 * Get the feedback contact info from branding configuration or use default
 * Returns an object with the contact value and whether it's an email or URL
 */
const getFeedbackContact = (
    currentLanguage: string
): { value: string; isEmail: boolean } => {
    const brandingConfig = getCustomBrandingConfig();

    if (brandingConfig) {
        const localizedStrings =
            brandingConfig.localizedStrings[currentLanguage];

        if (localizedStrings?.contactLink) {
            const contactLink = localizedStrings.contactLink;

            // If it's a mailto: URL, extract the email
            if (contactLink.startsWith("mailto:")) {
                return {
                    value: contactLink.replace("mailto:", ""),
                    isEmail: true,
                };
            }

            // If it starts with http/https, it's a URL
            if (
                contactLink.startsWith("http://") ||
                contactLink.startsWith("https://")
            ) {
                return {
                    value: contactLink,
                    isEmail: false,
                };
            }

            // Otherwise assume it's an email address
            return {
                value: contactLink,
                isEmail: true,
            };
        }
    }

    // Fall back to default email address
    return {
        value: FEEDBACK_EMAIL_ADDRESS,
        isEmail: true,
    };
};

const ContactLinkButton = (props: {
    children?: React.ReactNode;
    contact: string;
    isEmail: boolean;
}) => {
    const { t } = useTranslation();

    // If it's an email, show copy button with mailto link
    if (props.isEmail) {
        return (
            <CopyButton
                copyText={props.contact}
                successPopoverText={t(
                    "toolbar.item.user.content.tab.feedback.email.popover.success"
                )}
                errorPopoverText={t(
                    "toolbar.item.user.content.tab.feedback.email.popover.failed"
                )}
                copyIconAriaLabel={t(
                    "toolbar.item.user.content.tab.feedback.email.copy.button.ariaLabel"
                )}
            >
                <Link href={`mailto:${props.contact}`} target="_blank">
                    {props.children}
                </Link>
            </CopyButton>
        );
    }

    // If it's a URL, just show as external link without copy button
    return (
        <Link href={props.contact} external target="_blank">
            {props.children}
        </Link>
    );
};

/**
 * Profile - Feedback tab content
 */
const FeedbackTabContent = (): JSX.Element => {
    const { t, i18n } = useTranslation();
    const sessionId = useSessionDataStore((store) => store.sessionID);
    const contactLink = getFeedbackContact(i18n.language);

    // Check if customer contact link is provided
    const hasCustomerContact = contactLink.value !== FEEDBACK_EMAIL_ADDRESS;

    // Use generic strings when customer contact is provided
    const feedbackDescriptionKey = hasCustomerContact
        ? "toolbar.item.user.content.tab.feedback.brandingDescription"
        : "toolbar.item.user.content.tab.feedback.description";

    return (
        <div data-testid={"feedback-tab"}>
            <SpaceBetween size="l">
                <Trans
                    i18nKey={feedbackDescriptionKey}
                    values={{
                        emailAddress: contactLink.value,
                    }}
                >
                    {[
                        <ContactLinkButton
                            contact={contactLink.value}
                            isEmail={contactLink.isEmail}
                        ></ContactLinkButton>,
                    ]}
                </Trans>
                {!hasCustomerContact && (
                    <Box>
                        <Trans i18nKey="toolbar.item.user.content.tab.feedback.sessionId.description"></Trans>
                    </Box>
                )}
                <ValueWithLabel
                    label={t(
                        "toolbar.item.user.content.tab.feedback.sessionId.label"
                    )}
                >
                    {sessionId == null ? (
                        <Spinner
                            data-testid={"loading-session-data-sessionId"}
                        />
                    ) : (
                        <>
                            <CopyButton
                                copyText={sessionId}
                                successPopoverText={
                                    <Trans
                                        i18nKey={
                                            "toolbar.item.user.content.tab.feedback.sessionId.popover.success"
                                        }
                                    />
                                }
                                errorPopoverText={
                                    <Trans
                                        i18nKey={
                                            "toolbar.item.user.content.tab.feedback.sessionId.popover.failed"
                                        }
                                    />
                                }
                                copyIconAriaLabel={t(
                                    "toolbar.item.user.content.tab.feedback.sessionId.copy.button.ariaLabel"
                                )}
                            />
                            <>{sessionId}</>
                        </>
                    )}
                </ValueWithLabel>
            </SpaceBetween>
        </div>
    );
};

export const useProfileToolbarItem = (
    appStreamSDK: AppStreamSDK,
    toolbarState: string
): IToolbarButtonProps | IToolbarButtonStatefulModalProps => {
    const { t } = useTranslation();

    const activeTabId = useToolbarProfileTabIdStore((store) => store.activeId);
    const setActiveTabId = useToolbarProfileTabIdStore(
        (store) => store.setActiveId
    );
    const setUserEndSession = useSessionDataStore(
        (store) => store.setUserEndSession
    );
    const { setCollapsed } = useToolbarCollapseStore();

    // Emit session performance metrics every minute
    useEmitSessionPerformanceMetrics();

    const ProfileContent = () => {
        const { i18n } = useTranslation();
        const brandingConfig = getCustomBrandingConfig();
        const isBrandingEnabled = !!brandingConfig;

        // Determine if feedback tab should be shown
        const shouldShowFeedback =
            !isBrandingEnabled ||
            !!brandingConfig?.localizedStrings[i18n.language]?.contactLink;

        // Build tabs array conditionally
        const tabs = [
            {
                label: t(
                    "toolbar.item.user.content.tab.sessionPerformance.label"
                ),
                id: ToolbarProfileTabId.SESSION_PERFORMANCE,
                content: <SessionPerformanceTabContent />,
            },
            ...(shouldShowFeedback
                ? [
                      {
                          label: t(
                              "toolbar.item.user.content.tab.feedback.label"
                          ),
                          id: ToolbarProfileTabId.FEEDBACK,
                          content: <FeedbackTabContent />,
                      },
                  ]
                : []),
            {
                label: t("toolbar.item.user.content.tab.help.label"),
                id: ToolbarProfileTabId.HELP,
                content: <HelpTabContent />,
            },
            ...(!isBrandingEnabled
                ? [
                      {
                          label: getRebrandedTranslation(
                              "toolbar.item.user.content.tab.about.label",
                              t
                          ),
                          id: ToolbarProfileTabId.ABOUT,
                          content: <AboutTabContent />,
                      },
                  ]
                : []),
        ];

        return (
            <>
                <SpaceBetween direction={"horizontal"} size={"m"}>
                    {toolbarState === ToolbarMode.Floating && (
                        <Header>
                            <Trans i18nKey={"toolbar.item.user.label"} />
                        </Header>
                    )}
                    <Button
                        variant="primary"
                        onClick={() => {
                            setUserEndSession(true);
                            appStreamSDK.endSession();
                        }}
                    >
                        <Trans
                            i18nKey={
                                "toolbar.item.user.content.signOut.button.label"
                            }
                        />
                    </Button>
                </SpaceBetween>
                <Tabs
                    onChange={({ detail }) => {
                        setActiveTabId(
                            detail.activeTabId as ToolbarProfileTabId
                        );
                    }}
                    activeTabId={activeTabId}
                    tabs={tabs}
                />
            </>
        );
    };

    if (toolbarState === ToolbarMode.Docked) {
        return {
            type: IToolbarButtonType.STATEFUL,
            activeStateContentType: IToolbarButtonActiveContentType.MODAL,
            id: ToolbarItemId.PROFILE,
            label: t("toolbar.item.user.label"),
            icon: <Icon data-testid="profile-icon" name="user-profile" />,
            onClick: () => {
                setCollapsed(false);
            },
            ActiveStateContent: memo(
                (props: ToolbarActiveStateContentProps) => <ProfileContent />
            ),
            ariaLabelCloseModal: t("toolbar.item.user.content.close.ariaLabel"),
        };
    }

    return {
        type: IToolbarButtonType.STATEFUL,
        activeStateContentType: IToolbarButtonActiveContentType.CONTAINER,
        id: ToolbarItemId.PROFILE,
        label: t("toolbar.item.user.label"),
        ariaLabel: t("toolbar.item.user.ariaLabel"),
        icon: <Icon data-testid="profile-icon" name="user-profile" />,
        onClick: () => {
            setCollapsed(false);
        },
        ActiveStateContent: memo((props: ToolbarActiveStateContentProps) => (
            <ToolbarContainerItemContent
                close={props.close}
                closeButtonAriaLabel={t(
                    "toolbar.item.user.content.close.ariaLabel"
                )}
                closeButtonTitle={t("toolbar.item.content.close.title")}
            >
                <ProfileContent />
            </ToolbarContainerItemContent>
        )),
    };
};
